import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders } from '@fortawesome/free-solid-svg-icons';
import ListingCard from './ListingCard';

// ─── Titles for expanded category pages ─────────────────────────────────────

export const expandedCategoryTitles = (categoryKey, getLocalizedText) => {
  switch (categoryKey) {
    case 'rooms':
      return getLocalizedText('إعلانات الغرف', 'Available rooms in shared apartements');
    case 'foundFlat':
      return getLocalizedText('لقيت شقة وعايز فريق', 'Found a flat to share and rent');
    case 'searching':
      return getLocalizedText('بدور على شقة وفريق', 'Searching for a flat to share and rent');
    default:
      return '';
  }
};

// ─── شريط الصفحات (Pagination) ──────────────────────────────────────────────

function getPageNumbers(current, total) {
  const delta = 1;
  const range = [];
  const rangeWithDots = [];
  let last;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  range.forEach((i) => {
    if (last) {
      if (i - last === 2) {
        rangeWithDots.push(last + 1);
      } else if (i - last > 2) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    last = i;
  });

  return rangeWithDots;
}

function PaginationBar({ page, totalPages, onPageChange }) {
  const pages = getPageNumbers(page, totalPages);

  return (
    <div className="listings-pagination">
      <button
        type="button"
        className="pagination-nav-btn"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label="prev page"
      >
        ‹
      </button>

      {pages.map((p, idx) =>
        p === '...' ? (
          <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
        ) : (
          <button
            key={p}
            type="button"
            className={`pagination-page-btn ${p === page ? 'active' : ''}`}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        )
      )}

      <button
        type="button"
        className="pagination-nav-btn"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        aria-label="next page"
      >
        ›
      </button>
    </div>
  );
}

// ─── شاشة "Show More" الموسّعة: جريد كامل لكل إعلانات القسم + صفحات ─────────

function ExpandedCategoryGrid({
  title,
  listings,
  loading,
  error,
  page,
  pageSize,
  onPageChange,
  onBack,
  currentLanguage,
  getLocalizedText,
  favouriteIds,
  onToggleFavorite,
  categoryKey,
  onOpenFilters,
  activeFilterCount = 0,
}) {
  const totalPages = Math.max(1, Math.ceil(listings.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageListings = listings.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <section className="listings-expanded-section">
      <div className="listings-expanded-header">
        <button type="button" className="listings-expanded-back-btn" onClick={onBack}>
          <span className="listings-expanded-back-arrow">{getLocalizedText('›', '‹')}</span>
          {getLocalizedText('الرجوع', 'Back')}
        </button>
        <h2 className="listings-expanded-title">{title}</h2>
        <button
          type="button"
          className="listings-expanded-filter-btn"
          aria-label={getLocalizedText('فلتر', 'Filter')}
          onClick={onOpenFilters}
        >
          <FontAwesomeIcon icon={faSliders} />
          {activeFilterCount > 0 && (
            <span className="listings-expanded-filter-badge">{activeFilterCount}</span>
          )}
        </button>
      </div>

      {loading ? (
        <p className="listings-status-message">
          {getLocalizedText('جاري تحميل الإعلانات...', 'Loading ads...')}
        </p>
      ) : error ? (
        <p className="listings-status-message">
          {getLocalizedText('حدث خطأ في تحميل الإعلانات', 'Error loading ads')}
        </p>
      ) : listings.length === 0 ? (
        <p className="listings-status-message">
          {getLocalizedText('لا توجد إعلانات في هذا القسم حاليًا.', 'No listings in this section yet.')}
        </p>
      ) : (
        <>
          <div className="listings-expanded-grid">
            {pageListings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                currentLanguage={currentLanguage}
                getLocalizedText={getLocalizedText}
                favouriteIds={favouriteIds}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <PaginationBar page={safePage} totalPages={totalPages} onPageChange={onPageChange} />
          )}
        </>
      )}
    </section>
  );
}

export default ExpandedCategoryGrid;
